import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  Plane, Users, Mic, Clock, BookOpen, Zap, Shield, 
  Volume2, GraduationCap, Trophy, Star, ChevronRight,
  PlayCircle, Target, Award, Flame, Crown, Sparkles,
  AlertTriangle, MessageSquare, Timer, Headphones,
  Globe, Languages, Flag, CheckCircle, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dil seçenekleri kaldırıldı - sadece İngilizce aktif

export default function CabinCrewDashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [stats, setStats] = useState({
    totalLessons: 0,
    completedLessons: 0,
    totalMiles: 0,
    streak: 0,
    level: 'A1-A2'
  });

  // Demo kullanıcılar için erişilebilir modüller
  const isDemo = user?.is_demo || false;
  const demoAllowedModules = ['flashcards', 'vocabulary', 'timed', 'voice', 'roleplay'];

  useEffect(() => {
    if (user) {
      loadProgress();
    }
  }, [user]);

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cabin-crew/progress`);
      setProgress(response.data);
      
      const levelProgress = response.data.level_progress || {};
      let completed = 0;
      let total = 0;
      
      Object.values(levelProgress).forEach(level => {
        completed += level.completed || 0;
        total += level.total || 0;
      });
      
      // Get activity counts for better stats
      const activityCounts = response.data.activity_counts || {};
      const totalActivities = 
        (activityCounts.exams || 0) + 
        (activityCounts.speaking || 0) + 
        (activityCounts.reading || 0) + 
        (activityCounts.writing || 0) + 
        (activityCounts.listening || 0) + 
        (activityCounts.exercises || 0) + 
        (activityCounts.cabin_lessons || 0);
      
      setStats({
        totalLessons: total || 22,
        completedLessons: completed || totalActivities, // Show activities if no completed lessons
        totalMiles: response.data.total_miles || 0,
        streak: response.data.streak || user?.current_streak || 0,
        level: determineLevel(completed || totalActivities),
        activityCounts: activityCounts
      });
    } catch (error) {
      console.error('Progress load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const determineLevel = (completed) => {
    if (completed >= 18) return 'C1-C2';
    if (completed >= 10) return 'B1-B2';
    return 'A1-A2';
  };

  const overallProgress = stats.totalLessons > 0 
    ? Math.round((stats.completedLessons / stats.totalLessons) * 100) 
    : 0;

  const milestones = [
    { percent: 0, label: 'Başlangıç', icon: '🌱' },
    { percent: 25, label: 'Öğrenci', icon: '📚' },
    { percent: 50, label: 'Pratikçi', icon: '✈️' },
    { percent: 75, label: 'Uzman', icon: '🎖️' },
    { percent: 100, label: 'Kabin Memuru', icon: '👨‍✈️' }
  ];

  const currentMilestone = milestones.reduce((prev, curr) => 
    overallProgress >= curr.percent ? curr : prev
  , milestones[0]);

  const nextMilestone = milestones.find(m => m.percent > overallProgress) || milestones[milestones.length - 1];

  const features = [
    {
      id: 'level-test',
      title: 'Seviye Tespit Sınavı',
      description: 'İngilizce seviyeni öğren',
      icon: Target,
      color: 'from-indigo-500 to-blue-600',
      badge: 'Sınav',
      path: '/exam/english',
      demoLocked: false
    },
    {
      id: 'grammar',
      title: 'Gramer Eğitimleri',
      description: 'Temel & ileri gramer konuları',
      icon: BookOpen,
      color: 'from-emerald-500 to-teal-600',
      badge: 'Dil',
      path: '/languages',
      demoLocked: false
    },
    {
      id: 'comprehensive',
      title: '64 Soruluk Eğitim',
      description: '8 aşamalı kapsamlı eğitim programı',
      icon: GraduationCap,
      color: 'from-sky-500 to-blue-600',
      badge: '8 Aşama',
      path: '/cabin-crew/modules',
      demoLocked: false
    },
    {
      id: 'flashcards',
      title: 'Flashcard Modu',
      description: 'Hızlı kelime öğrenme',
      icon: Zap,
      color: 'from-amber-500 to-orange-600',
      badge: 'Yeni',
      path: '/cabin-crew/flashcards',
      demoLocked: false
    },
    {
      id: 'drag-drop',
      title: 'Sürükle & Bırak',
      description: 'Etkileşimli eşleştirme',
      icon: Target,
      color: 'from-green-500 to-emerald-600',
      badge: 'Etkileşimli',
      path: '/cabin-crew/drag-drop',
      demoLocked: false
    },
    {
      id: 'voice',
      title: 'Sesli Pratik',
      description: 'Anons okuma & değerlendirme',
      icon: Mic,
      color: 'from-pink-500 to-rose-600',
      badge: 'AI',
      path: '/cabin-crew/voice-practice',
      demoLocked: false
    },
    {
      id: 'timed',
      title: 'Zamanlı Yarışma',
      description: 'ICAO & kelime hız testi',
      icon: Timer,
      color: 'from-purple-500 to-violet-600',
      badge: 'Yarışma',
      path: '/cabin-crew/timed-challenge',
      demoLocked: false
    },
    {
      id: 'roleplay',
      title: 'Rol-Play',
      description: 'Yolcu ile etkileşim simülasyonu',
      icon: MessageSquare,
      color: 'from-cyan-500 to-teal-600',
      badge: 'Simülasyon',
      path: '/cabin-crew/roleplay',
      demoLocked: false
    },
    {
      id: 'interview',
      title: 'Mülakat Simülasyonu',
      description: 'Gerçek mülakat deneyimi',
      icon: Users,
      color: 'from-indigo-500 to-purple-600',
      badge: 'Pro',
      path: '/cabin-crew/interview',
      demoLocked: false
    },
    {
      id: 'emergency',
      title: 'Acil Durum',
      description: 'Acil durum prosedürleri',
      icon: AlertTriangle,
      color: 'from-red-500 to-orange-600',
      badge: 'Kritik',
      path: '/cabin-crew/emergency',
      demoLocked: false
    }
  ];

  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Plane className="w-16 h-16 text-sky-500 animate-bounce mx-auto mb-4" />
            <p className="text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  return (
    <CabinCrewLayout>
      <div className="min-h-screen">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700 text-white">
          {/* Animated background */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 animate-pulse text-4xl">✈️</div>
            <div className="absolute top-20 right-20 animate-bounce text-3xl">☁️</div>
            <div className="absolute bottom-10 left-1/4 animate-pulse delay-100 text-4xl">✈️</div>
            <div className="absolute top-1/3 right-1/3 animate-bounce delay-200 text-3xl">☁️</div>
          </div>
          
          <div className="container mx-auto px-6 py-8 relative z-10">
            {/* Welcome Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <span className="text-5xl">✈️</span>
              </div>
              <div>
                <p className="text-sky-200 text-sm">Merhaba {user?.name || 'Pilot'}! 👋</p>
                <h1 className="text-3xl md:text-4xl font-black">
                  Kabin Memurluğu Eğitimi
                </h1>
                <p className="text-sky-100">
                  Havacılık İngilizcesi & Profesyonel Gelişim
                </p>
              </div>
            </div>

            {/* Progress Card */}
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{currentMilestone.icon}</span>
                    <div>
                      <p className="text-sm text-sky-100">Mevcut Seviye</p>
                      <p className="text-xl font-bold">{currentMilestone.label}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-sky-100">Hedef</p>
                    <p className="text-xl font-bold flex items-center gap-2">
                      {nextMilestone.icon} {nextMilestone.label}
                    </p>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="relative mb-2">
                  <div className="h-4 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 rounded-full transition-all duration-1000"
                      style={{ width: `${overallProgress}%` }}
                    />
                  </div>
                  <div className="absolute inset-0 flex justify-between items-center px-1">
                    {milestones.map((m, idx) => (
                      <div 
                        key={idx}
                        className={`w-3 h-3 rounded-full ${
                          overallProgress >= m.percent ? 'bg-yellow-400' : 'bg-white/30'
                        }`}
                        title={m.label}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-sky-100">
                    {stats.completedLessons}/{stats.totalLessons} Ders Tamamlandı
                  </span>
                  <span className="font-bold text-yellow-300">
                    %{overallProgress} Kabin Memurluğuna Yaklaştın!
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Stats Row */}
        <div className="container mx-auto px-6 -mt-6 relative z-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-white shadow-lg border-0">
              <CardContent className="p-4 text-center">
                <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-black text-gray-800">{stats.totalMiles}</p>
                <p className="text-xs text-gray-500">Toplam Mil</p>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-lg border-0">
              <CardContent className="p-4 text-center">
                <Flame className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-black text-gray-800">{stats.streak}</p>
                <p className="text-xs text-gray-500">Gün Serisi</p>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-lg border-0">
              <CardContent className="p-4 text-center">
                <BookOpen className="w-8 h-8 text-sky-500 mx-auto mb-2" />
                <p className="text-2xl font-black text-gray-800">{stats.completedLessons}</p>
                <p className="text-xs text-gray-500">Tamamlanan</p>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-lg border-0">
              <CardContent className="p-4 text-center">
                <Star className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-black text-gray-800">{stats.level}</p>
                <p className="text-xs text-gray-500">Seviye</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Anlık İstatistikler & Liderlik Tablosu */}
        <div className="container mx-auto px-6 py-4">
          {/* Anlık Çevrimiçi */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-0">
              <CardContent className="p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-600 font-medium">Çevrimiçi</span>
                </div>
                <p className="text-xl font-bold text-green-700">{Math.floor(Math.random() * 50) + 127}</p>
                <p className="text-xs text-green-600">Aktif Öğrenci</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-0">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-blue-700">2,847</p>
                <p className="text-xs text-blue-600">Toplam Üye</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-0">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-purple-700">156K</p>
                <p className="text-xs text-purple-600">Çözülen Soru</p>
              </CardContent>
            </Card>
          </div>

          {/* Liderlik Tablosu */}
          <Card className="bg-white shadow-lg border-0 mb-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-bold text-gray-800">Haftalık Liderler</h3>
                </div>
                <Badge className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5">
                  🏆 1. = 500 Mil
                </Badge>
              </div>
              <div className="space-y-2">
                {[
                  { rank: 1, name: 'Elif Y.', xp: 2840, badge: '🥇' },
                  { rank: 2, name: 'Ahmet K.', xp: 2650, badge: '🥈' },
                  { rank: 3, name: 'Zeynep D.', xp: 2420, badge: '🥉' },
                  { rank: 4, name: 'Mehmet Ö.', xp: 2180, badge: '4' },
                  { rank: 5, name: 'Ayşe Ç.', xp: 1950, badge: '5' },
                ].map((player, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-2 p-2 rounded-lg ${
                      player.rank <= 3 ? 'bg-yellow-50' : 'bg-gray-50'
                    }`}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      player.rank === 1 ? 'bg-yellow-400 text-white' :
                      player.rank === 2 ? 'bg-gray-400 text-white' :
                      player.rank === 3 ? 'bg-orange-400 text-white' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {player.rank <= 3 ? player.badge : player.rank}
                    </span>
                    <span className="flex-1 text-sm font-medium text-gray-700">{player.name}</span>
                    <span className="text-sm font-bold text-purple-600">{player.xp.toLocaleString()}</span>
                  </div>
                ))}
                
                {/* Kullanıcı */}
                <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-100 border border-blue-300 mt-2">
                  <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                    {Math.floor(Math.random() * 15) + 18}
                  </span>
                  <span className="flex-1 text-sm font-medium text-blue-800">
                    {user?.name?.split(' ')[0] || 'Sen'} <span className="text-xs text-blue-600">(Sen)</span>
                  </span>
                  <span className="text-sm font-bold text-blue-700">{stats.totalMiles || 450}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Learning Path Preview */}
        <div className="container mx-auto px-6 pb-4">
          <Card 
            className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white border-0 cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/learning-path')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Target className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-xl font-bold">Öğrenme Yol Haritası</h3>
                      <Badge className="bg-white/20 text-white text-xs">Yeni</Badge>
                    </div>
                    <p className="text-indigo-100 text-sm">
                      3 Aşama: Başlangıç → Orta → İleri | 10 Modül ile Kabin Memurluğuna
                    </p>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-400 rounded-full" />
                    <span className="text-xs">A1-A2</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-indigo-200" />
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-amber-400 rounded-full" />
                    <span className="text-xs">B1-B2</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-indigo-200" />
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-red-400 rounded-full" />
                    <span className="text-xs">C1-C2</span>
                  </div>
                </div>
                <ArrowRight className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Feature Cards */}
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-6 h-6 text-amber-500" />
            <h2 className="text-2xl font-black text-gray-800">Eğitim Modülleri</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature) => {
              const Icon = feature.icon;
              const isLocked = isDemo && feature.demoLocked;
              return (
                <Card 
                  key={feature.id}
                  className={`group transition-all border-2 border-transparent ${
                    isLocked 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'cursor-pointer hover:shadow-xl hover:-translate-y-1 hover:border-sky-300'
                  }`}
                  onClick={() => !isLocked && navigate(feature.path)}
                >
                  <CardContent className="p-5 relative">
                    {isLocked && (
                      <div className="absolute top-2 right-2 bg-gray-800 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        🔒 Premium
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 bg-gradient-to-br ${isLocked ? 'from-gray-400 to-gray-500' : feature.color} rounded-xl flex items-center justify-center shadow-lg ${!isLocked && 'group-hover:scale-110'} transition-transform`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      {!isLocked && (
                        <Badge className={`bg-gradient-to-r ${feature.color} text-white text-xs`}>
                          {feature.badge}
                        </Badge>
                      )}
                    </div>
                    <h3 className={`font-bold mb-1 ${isLocked ? 'text-gray-500' : 'text-gray-800'}`}>{feature.title}</h3>
                    <p className={`text-sm mb-3 ${isLocked ? 'text-gray-400' : 'text-gray-500'}`}>{feature.description}</p>
                    <div className={`flex items-center text-sm font-medium ${isLocked ? 'text-gray-400' : 'text-sky-600'}`}>
                      {isLocked ? 'Tam sürümde mevcut' : 'Başla'} 
                      {!isLocked && <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Level Selection */}
        <div className="container mx-auto px-6 pb-8">
          <div className="flex items-center gap-3 mb-6">
            <GraduationCap className="w-6 h-6 text-indigo-500" />
            <h2 className="text-2xl font-black text-gray-800">Seviye Seçin</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* A1-A2 */}
            <Card 
              className="cursor-pointer hover:shadow-xl transition-all border-4 border-green-300 bg-gradient-to-br from-green-50 to-emerald-50"
              onClick={() => navigate('/cabin-crew/modules?level=A1-A2')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center">
                    <span className="text-3xl">🌱</span>
                  </div>
                  <div>
                    <Badge className="bg-green-500 text-white mb-1">Başlangıç</Badge>
                    <h3 className="text-xl font-black text-gray-800">A1-A2</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Temel havacılık kelimeleri, basit diyaloglar, ICAO alfabesi
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">7 Modül</span>
                  <ChevronRight className="w-5 h-5 text-green-500" />
                </div>
              </CardContent>
            </Card>

            {/* B1-B2 */}
            <Card 
              className="cursor-pointer hover:shadow-xl transition-all border-4 border-sky-300 bg-gradient-to-br from-sky-50 to-blue-50"
              onClick={() => navigate('/cabin-crew/modules?level=B1-B2')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-sky-400 to-blue-500 rounded-2xl flex items-center justify-center">
                    <span className="text-3xl">✈️</span>
                  </div>
                  <div>
                    <Badge className="bg-sky-500 text-white mb-1">Orta Seviye</Badge>
                    <h3 className="text-xl font-black text-gray-800">B1-B2</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Profesyonel iletişim, anonslar, acil durumlar, mülakat hazırlığı
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">8 Modül</span>
                  <ChevronRight className="w-5 h-5 text-sky-500" />
                </div>
              </CardContent>
            </Card>

            {/* C1-C2 */}
            <Card 
              className="cursor-pointer hover:shadow-xl transition-all border-4 border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50"
              onClick={() => navigate('/cabin-crew/modules?level=C1-C2')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-2xl flex items-center justify-center">
                    <span className="text-3xl">👨‍✈️</span>
                  </div>
                  <div>
                    <Badge className="bg-purple-500 text-white mb-1">İleri Seviye</Badge>
                    <h3 className="text-xl font-black text-gray-800">C1-C2</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Liderlik, kültürlerarası iletişim, ileri mülakat, kriz yönetimi
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">7 Modül</span>
                  <ChevronRight className="w-5 h-5 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Motivational Footer */}
        <div className="bg-gradient-to-r from-sky-600 to-indigo-700 text-white py-8">
          <div className="container mx-auto px-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Crown className="w-8 h-8 text-yellow-400" />
              <h3 className="text-2xl font-black">Hayalindeki Kariyere Uç!</h3>
              <Crown className="w-8 h-8 text-yellow-400" />
            </div>
            <p className="text-sky-100 max-w-2xl mx-auto">
              Her gün biraz daha yaklaşıyorsun. Düzenli çalış, pratik yap ve 
              kabin memurluğu hayaline kavuş! 🛫
            </p>
          </div>
        </div>
      </div>
    </CabinCrewLayout>
  );
}
